import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { get, post, ApiError } from '../../lib/api';

const STORES = ['BH21101', 'BH21102', 'BH21103'];
const NAV_RE = /^F[A-Z](\d{2})-/i;
const navControlled = (sku: string) => {
  const m = NAV_RE.exec(sku);
  return !!m && Number(m[1]) >= 26;
};

interface Row {
  variantId: string; sku: string; barcode: string | null; size: string | null;
  colour: string | null; priceMinor: number; titleEn: string; imageUrl: string | null;
  stock: Record<string, number>;
}

export default function StockAdjust() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [term, setTerm] = useState('');
  const [edits, setEdits] = useState<Record<string, string>>({}); // `${variantId}:${store}` -> qty
  const [reason, setReason] = useState('Stock count correction');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const search = useQuery({
    queryKey: ['stock-search', term],
    queryFn: () => get<any>(`/api/v1/lookup?q=${encodeURIComponent(term)}&limit=25`),
    enabled: !!term,
  });

  const adjust = useMutation({
    mutationFn: (v: { sku: string; storeCode: string; onHand: number; reason: string }) =>
      post<any>('/api/v1/admin/stock/adjust', v),
    onSuccess: (r) => {
      setMsg({ ok: true, text: `${r.sku} @ ${r.storeCode}: ${r.before} → ${r.after}${r.warning ? ' — ' + r.warning : ''}` });
      void qc.invalidateQueries({ queryKey: ['stock-search', term] });
    },
    onError: (e) => setMsg({ ok: false, text: e instanceof ApiError ? e.message : 'Adjust failed' }),
  });

  const rows: Row[] = search.data?.results ?? [];

  return (
    <div className="p-6 space-y-4 max-w-6xl">
      <div>
        <h1 className="text-xl font-semibold">Stock adjustment</h1>
        <p className="text-sm text-gray-400 mt-1">
          Search any SKU, barcode or product name, then set the on-hand quantity per store.
          Every change is written to the stock-movement audit trail with your reason.
        </p>
      </div>

      <div className="flex gap-2">
        <input className="input flex-1 max-w-md" placeholder="SKU, barcode or product name…"
          value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && setTerm(q.trim())} />
        <button className="btn" onClick={() => setTerm(q.trim())}>Search</button>
        <input className="input w-64" placeholder="Reason (required)"
          value={reason} onChange={(e) => setReason(e.target.value)} />
      </div>

      {msg && (
        <div className={`text-sm rounded-lg px-4 py-2 border ${msg.ok
          ? 'text-emerald-300 bg-emerald-950/50 border-emerald-900'
          : 'text-red-300 bg-red-950/50 border-red-900'}`}>{msg.text}</div>
      )}

      {term && (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr>
              <th className="th">Variant</th>
              {STORES.map((s) => <th key={s} className="th text-center">{s}</th>)}
              <th className="th"></th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.variantId} className="border-t border-gray-800">
                  <td className="td">
                    <div className="flex items-center gap-2">
                      {r.imageUrl && <img src={r.imageUrl} className="w-8 h-10 object-cover rounded bg-gray-800" alt="" />}
                      <div className="min-w-0">
                        <div className="truncate max-w-[220px]">{r.titleEn}</div>
                        <div className="text-xs text-gray-500 font-mono">{r.sku}
                          {navControlled(r.sku) && (
                            <span className="ml-1 text-amber-400" title="NAV owns this SKU's quantity — the next sync overwrites manual edits">· NAV</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  {STORES.map((s) => {
                    const key = `${r.variantId}:${s}`;
                    const cur = r.stock?.[s] ?? 0;
                    const val = edits[key] ?? String(cur);
                    const dirty = val !== String(cur);
                    return (
                      <td key={s} className="td text-center">
                        <input type="number" min={0}
                          className={`input w-20 text-center ${dirty ? 'border-brand' : ''}`}
                          value={val}
                          onChange={(e) => setEdits({ ...edits, [key]: e.target.value })} />
                      </td>
                    );
                  })}
                  <td className="td text-right">
                    {STORES.some((s) => (edits[`${r.variantId}:${s}`] ?? String(r.stock?.[s] ?? 0)) !== String(r.stock?.[s] ?? 0)) && (
                      <button className="btn text-xs" disabled={adjust.isPending || reason.trim().length < 3}
                        onClick={() => {
                          for (const s of STORES) {
                            const key = `${r.variantId}:${s}`;
                            const cur = r.stock?.[s] ?? 0;
                            const v = edits[key];
                            if (v !== undefined && v !== String(cur)) {
                              adjust.mutate({ sku: r.sku, storeCode: s, onHand: Math.max(0, Math.trunc(Number(v) || 0)), reason: reason.trim() });
                            }
                          }
                        }}>Save</button>
                    )}
                  </td>
                </tr>
              ))}
              {!rows.length && !search.isFetching && (
                <tr><td className="td text-center text-gray-500 py-8" colSpan={5}>No matches for “{term}”</td></tr>
              )}
              {search.isFetching && (
                <tr><td className="td text-center text-gray-500 py-8" colSpan={5}>Searching…</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-gray-500">
        Rows marked <span className="text-amber-400">NAV</span> are season ≥ 26 — NAV owns their
        quantity and will overwrite this on the next sync. For everything else this edit is the truth.
      </p>
    </div>
  );
}
